import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { getFlatNavItems } from 'selectors/navigationSelectors'
import useLocalizedNavItems from 'hooks/useLocalizedNavItems'
import styled from 'styled-components'
import { rem } from 'polished'
import NavBarTab from '../NavBarTab/NavBarTab'
import Group from 'components/utils/Group'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import useToggle from 'hooks/useToggle'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'
import { useIntl } from 'react-intl'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import useResizeObserver from 'hooks/useResizeObserver'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { WEDDING_GIFT_REGISTRY_NAV_ITEM_IDS } from 'weddings/constants/giftRegistry'

const MoreNavTab = styled(NavBarTab)`
  flex-shrink: 0;
`

const ProductTabsContainer = styled.div<{ $allowOverflow?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  position: relative;
  overflow: ${props => props.$allowOverflow ? 'visible' : 'hidden'};
`

const ProductTabs = styled(Group)`
  min-width: 0;
  max-width: 100%;

  & > * {
    flex-shrink: 0;
  }
`

const MeasurementTabs = styled(ProductTabs)`
  position: absolute;
  inset-inline-start: 0;
  top: 0;
  height: 0;
  visibility: hidden;
  pointer-events: none;
`

const NavListItem = styled(ClickableListItem)`
  display: flex;
`

const NewLabelNavListItem = styled(NavListItem)`
  &::after {
    content: attr(data-new-label);
    flex-shrink: 0;
    background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
    color: ${props => props.theme.palette.messaging.critical.normalContrast};
    padding: ${rem(2)} ${rem(8)};
    border-radius: ${props => props.theme.borderRadius.round};
    font-weight: ${props => props.theme.font.primary.weight.bold};
    font-size: ${rem(12)};
    line-height: ${rem(16)};
  }
`

const MORE_DROPDOWN_BODY_MAX_HEIGHT = rem(560)
const MAX_VISIBLE_PRODUCT_TABS = 10

interface VisibleProductCountOptions {
  availableWidth: number
  itemWidths: Array<number>
  moreWidth: number
}

export function getVisibleProductCount(options: VisibleProductCountOptions) {
  const { availableWidth, itemWidths, moreWidth } = options
  const maxVisibleItems = Math.min(MAX_VISIBLE_PRODUCT_TABS, itemWidths.length)
  const totalItemsWidth = itemWidths.reduce((sum, width) => sum + width, 0)

  if (totalItemsWidth <= availableWidth) {
    return maxVisibleItems
  }

  const availableProductWidth = Math.max(availableWidth - moreWidth, 0)
  let visibleWidth = 0

  for (const [index, width] of itemWidths.entries()) {
    if (index >= MAX_VISIBLE_PRODUCT_TABS) {
      return MAX_VISIBLE_PRODUCT_TABS
    }

    if (visibleWidth + width > availableProductWidth) {
      return index
    }

    visibleWidth += width
  }

  return maxVisibleItems
}

function NavBarProducts() {
  const intl = useIntl()
  const [open, toggleOpen, _, closeDropdown] = useToggle()
  const isMobile = useScreenSizeOnly('mobile')
  const moreTabRef = useRef<HTMLButtonElement>(null)
  const measureMoreTabRef = useRef<HTMLButtonElement>(null)
  const productTabsContainerRef = useRef<HTMLDivElement>(null)
  const measurementTabsRef = useRef<HTMLDivElement>(null)
  const measureNavTabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const moreDropdownBodyRef = useRef<HTMLDivElement>(null)
  const [visibleProductCount, setVisibleProductCount] = useState<number>()
  const rawNavs = useAppSelector(getFlatNavItems)
  const navs = useLocalizedNavItems(rawNavs)
  const showModal = useContext(ModalContext)
  const { isActive: isWeddingGiftRegistry } = useWeddingGiftRegistryContext()
  const showMoreOverflow = !isAgentHubEnabled
  const resolvedVisibleProductCount = showMoreOverflow ? (visibleProductCount ?? navs.length) : navs.length
  const visibleNavs = useMemo(() => navs.slice(0, resolvedVisibleProductCount), [navs, resolvedVisibleProductCount])
  const overflowNavs = useMemo(
    () => showMoreOverflow ? navs.slice(resolvedVisibleProductCount) : [],
    [navs, resolvedVisibleProductCount, showMoreOverflow],
  )
  const hasOverflowNavs = overflowNavs.length > 0

  const newLabelText = intl.formatMessage({
    id: 'ULXt7xsBZxh6l90xv4YH',
    defaultMessage: 'NEW',
    description: 'New badge label for navigation items in the site header',
  })

  const moreLabelText = intl.formatMessage({
    id: 'tu0LauZOJipCPjr6G-Vw',
    defaultMessage: 'More',
    description: 'Navigation link label for the more dropdown in the navigation bar',
  })

  const moreTab: App.NavItem = {
    text: moreLabelText,
    category: 'tertiary',
  }

  const weddingGiftRegistryNavs = useMemo(() => {
    if (!isWeddingGiftRegistry) {
      return []
    }

    return WEDDING_GIFT_REGISTRY_NAV_ITEM_IDS
      .map(navItemId => navs.find(nav => nav.id === navItemId))
      .filter((nav): nav is App.NavItem => !!nav)
  }, [isWeddingGiftRegistry, navs])

  const onNavClick = useCallback((_nav: App.NavItem, onClose?: () => void) => {
    if (onClose) {
      onClose()
    }
  }, [])

  const updateVisibleProductCount = useCallback(() => {
    const container = productTabsContainerRef.current
    const measureMoreTab = measureMoreTabRef.current

    if (!showMoreOverflow || !container || !measureMoreTab) {
      return
    }

    const itemWidths = measureNavTabRefs.current
      .slice(0, navs.length)
      .map(item => item?.getBoundingClientRect().width ?? 0)

    const nextVisibleProductCount = getVisibleProductCount({
      availableWidth: container.getBoundingClientRect().width,
      itemWidths,
      moreWidth: measureMoreTab.getBoundingClientRect().width,
    })

    setVisibleProductCount(currentVisibleProductCount => (
      currentVisibleProductCount === nextVisibleProductCount ? currentVisibleProductCount : nextVisibleProductCount
    ))
  }, [navs.length, showMoreOverflow])

  useResizeObserver(productTabsContainerRef, updateVisibleProductCount)
  useResizeObserver(measurementTabsRef, updateVisibleProductCount)

  useIsomorphicLayoutEffect(() => {
    updateVisibleProductCount()
  }, [updateVisibleProductCount, navs, moreTab.text])

  useEffect(() => {
    if (!hasOverflowNavs) {
      closeDropdown()
    }
  }, [closeDropdown, hasOverflowNavs])

  const onMoreClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (isMobile) {
      showModal(resolve => <Modal title={moreLabelText}>
        {overflowNavs.map(item => {
          const ListItem = item.newLabel ? NewLabelNavListItem : NavListItem
          return <ListItem
            size="large"
            key={item.id}
            title={item.text}
            to={item.to}
            href={item.href}
            data-new-label={item.newLabel ? newLabelText : undefined}
            onClick={() => onNavClick(item, resolve)}
          />
        })}
      </Modal>)
    } else {
      toggleOpen()
    }
  }, [isMobile, overflowNavs, onNavClick, showModal, toggleOpen, moreLabelText, newLabelText])

  useEffect(() => {
    if (!open || !moreDropdownBodyRef.current) {
      return
    }

    moreDropdownBodyRef.current.style.maxHeight = MORE_DROPDOWN_BODY_MAX_HEIGHT
  }, [open])

  if (isWeddingGiftRegistry) {
    return <Group direction="horizontal">
      <Group direction="horizontal">
        {weddingGiftRegistryNavs.map(nav => <NavBarTab
          key={nav.id}
          nav={nav}
          onClick={() => onNavClick(nav)}
        />)}
      </Group>
    </Group>
  }

  return <Group direction="horizontal" fullWidth>
    <ProductTabsContainer ref={productTabsContainerRef} $allowOverflow={!showMoreOverflow}>
      <ProductTabs direction="horizontal">
        {visibleNavs.map(nav => <NavBarTab
          key={nav.id}
          nav={nav}
          onClick={() => onNavClick(nav)}
        />)}
        {showMoreOverflow && <MoreNavTab
          onClick={onMoreClick}
          nav={moreTab}
          ref={moreTabRef}
          isDropdown
          hidden={!hasOverflowNavs}
        />}
      </ProductTabs>
      {showMoreOverflow && <MeasurementTabs direction="horizontal" aria-hidden ref={measurementTabsRef}>
        {navs.map((nav, index) => <NavBarTab
          key={nav.id}
          nav={nav}
          ref={(element) => {
            measureNavTabRefs.current[index] = element
          }}
          tabIndex={-1}
        />)}
        <MoreNavTab
          nav={moreTab}
          ref={measureMoreTabRef}
          isDropdown
          tabIndex={-1}
        />
      </MeasurementTabs>}
    </ProductTabsContainer>
    {showMoreOverflow && <DropdownList
      size="S"
      triggerRef={moreTabRef}
      anchorRef={moreTabRef}
      bodyRef={moreDropdownBodyRef}
      open={!isMobile && open && hasOverflowNavs}
      onClose={closeDropdown}
      portaled
    >
      {overflowNavs.map(item => {
        const ListItem = item.newLabel ? NewLabelNavListItem : NavListItem
        return <ListItem
          size="medium"
          key={item.id}
          title={item.text}
          to={item.to}
          href={item.href}
          data-new-label={item.newLabel ? newLabelText : undefined}
          onClick={() => onNavClick(item, closeDropdown)}
        />
      })}
    </DropdownList>}
  </Group>
}

export default memo(NavBarProducts)
